import {
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  Button,
  ModalFooter,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormikInput, FormikDatepicker, FormikCheckbox } from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import { dateBetweenTwoDates } from 'containers/TimesheetMileage/shared/helpers';
import { MONTH_DAY_YEAR_TIME_FORMAT } from 'global/appConstants';
import { useSaveCaliBreakInClock } from 'hooks/caliBreakRules/useSaveCaliBreakInClock';
import { useTimeClockStore } from 'store/useTimeClockStore';
import toaster from 'utils/toaster';

import { breakFormValidationSchema, initialBreakModalFormValue } from './schema';

type IMealOrRestBreakModalProps = {
  isOpen: boolean;
  onClose: () => void;
  breakType: 'meal' | 'rest';
  handleSaveRestOrMealBreakSuccess: (breakDuration: number) => void;
};

const MealOrRestBreakModal: React.FC<IMealOrRestBreakModalProps> = ({
  isOpen,
  onClose,
  breakType,
  handleSaveRestOrMealBreakSuccess,
}) => {
  const { t } = useTranslation(['common', 'employees', 'timesheetMileage']);
  const timeClockDetails: any = useTimeClockStore(state => state.timeClock);

  const { status: saveCaliBreakStatus, mutateAsync: mutateAsyncCaliBreak } = useSaveCaliBreakInClock();

  const handleBreakDurationChange = (start: Date, end: Date, setFieldValue: any) => {
    const milliSecondDiff = end.getTime() - start.getTime();
    if (milliSecondDiff >= 0) {
      const minuteDiff = Math.floor(milliSecondDiff / 1000 / 60);

      setFieldValue('break_duration', minuteDiff);
    } else {
      toaster.error({
        description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
      });
    }
  };

  const validateBreakStart = (setFieldValue: any, breakStart: Date, breakEnd?: Date) => {
    const clockIn = timeClockDetails?.start ? new Date(timeClockDetails.start + 'Z') : new Date();
    const clockOut = new Date();

    if (!dateBetweenTwoDates(breakStart, clockIn, clockOut)) {
      setFieldValue('start', undefined);
      setFieldValue('break_duration', '');
      toaster.error({
        description: t('timesheetMileage:breakInTimeRangeBetweenClockInAndClockOutTime'),
      });
    } else {
      setFieldValue(`start`, breakStart);
      if (!!breakEnd) {
        if (dateBetweenTwoDates(breakEnd, clockIn, clockOut)) {
          handleBreakDurationChange(breakStart, breakEnd, setFieldValue);
        } else {
          toaster.error({
            description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
          });
        }
      }
    }
  };

  const validateBreakEnd = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    breakEnd: Date,
    breakStart: Date
  ) => {
    const clockIn = timeClockDetails?.start ? new Date(timeClockDetails.start + 'Z') : new Date();
    const clockOut = new Date();

    if (!dateBetweenTwoDates(breakEnd, clockIn, clockOut)) {
      setFieldValue('end', '');
      setFieldValue('break_duration', '');
      toaster.error({
        description: t('timesheetMileage:breakOutTimeRangeBetweenClockInAndClockOutTime'),
      });
    } else if (breakEnd?.getTime() - breakStart?.getTime() < 0) {
      setFieldValue('end', '');
      setFieldValue('break_duration', '');
      toaster.error({
        description: t('timesheetMileage:endBreakTimeCantBeLesserThanStartBreakTime'),
      });
    } else {
      setFieldValue('end', breakEnd);

      if (!!breakStart) {
        if (dateBetweenTwoDates(breakStart, clockIn, clockOut)) {
          handleBreakDurationChange(breakStart, breakEnd, setFieldValue);
        } else {
          toaster.error({
            description: t('timesheetMileage:breakInTimeRangeBetweenClockInAndClockOutTime'),
          });
        }
      }
    }
  };

  const handleBreakFormSubmit = async (values: typeof initialBreakModalFormValue) => {
    const postBreakData = {
      break_type: breakType,
      timesheet_id: timeClockDetails?.id,
      start: values.start ? values.start : '',
      end: values.end ? values.end : '',
      break_duration: values.break_duration ? values.break_duration * 60 : 0,
    };

    mutateAsyncCaliBreak(postBreakData).then(() => {
      handleSaveRestOrMealBreakSuccess(values.break_duration);
    });
  };

  return (
    <ControllableModal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalContent>
        <ModalHeader>Add {breakType == 'meal' ? t('employees:mealBreak') : t('employees:restBreak')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Formik
            enableReinitialize
            initialValues={initialBreakModalFormValue}
            validationSchema={breakFormValidationSchema}
            onSubmit={handleBreakFormSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form noValidate>
                <FormikInput
                  type="text"
                  label={t('employees:durationInMinutes')}
                  name="break_duration"
                  formControlProps={{
                    isRequired: true,
                    isDisabled: values.setBreakTime.length > 0,
                  }}
                />

                <FormikCheckbox
                  name="setBreakTime"
                  options={[{ label: t('employees:setBreakStartAndEndTime'), value: 'yes' }]}
                  onChangeCallback={() => {
                    if (values.start && values.end) {
                      handleBreakDurationChange(values.start as Date, values.end as Date, setFieldValue);
                    }
                  }}
                />

                {values.setBreakTime.length > 0 && (
                  <>
                    <FormControl>
                      <FormikDatepicker
                        label={t('common:startTime')}
                        name="start"
                        datepickerProps={{
                          showTimeInput: true,
                          popperPlacement: 'top',
                          shouldCloseOnSelect: false,
                          isClearable: false,
                          dateFormat: MONTH_DAY_YEAR_TIME_FORMAT,
                          maxDate: values.end && new Date(values.end) < new Date() ? new Date(values.end) : new Date(),
                          minDate: timeClockDetails?.start ? new Date(timeClockDetails.start + 'Z') : new Date(),
                        }}
                        handleChangeCallback={(startDate: Date) =>
                          validateBreakStart(setFieldValue, startDate, values.end as Date | undefined)
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormikDatepicker
                        label={t('common:endTime')}
                        name="end"
                        datepickerProps={{
                          showTimeInput: true,
                          popperPlacement: 'top',
                          shouldCloseOnSelect: false,
                          isClearable: false,
                          dateFormat: MONTH_DAY_YEAR_TIME_FORMAT,
                          minDate: values.start,
                          maxDate: new Date(),
                        }}
                        handleChangeCallback={(endDate: Date) =>
                          validateBreakEnd(setFieldValue, endDate, values.start as Date)
                        }
                      />
                    </FormControl>
                  </>
                )}

                <ModalFooter px="0">
                  <Button onClick={onClose} variant="outline">
                    {t('common:cancel')}
                  </Button>
                  <Button ml={3} type="submit" isLoading={saveCaliBreakStatus === 'loading'}>
                    {t('common:save')}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </ControllableModal>
  );
};

export default MealOrRestBreakModal;
